import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import SearchIcon from '@material-ui/icons/Search';
import {
  CheckCircle,
  Close,
  Help,
  Cancel,
  InfoOutlined,
  RotateLeft,
  Schedule,
} from '@material-ui/icons';
import { DatePicker, PageableTable, SnackbarContext } from 'components';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import {
  fetchAllMeetings,
  exportMeetings,
  fetchMeetingFilterOptions,
} from 'lib/Api/Meetings';
import {
  PagedMeeting,
  Meeting,
  MeetingFilterOptions,
} from 'lib/Model/Meetings';
import * as React from 'react';
import { stringAvatar } from 'lib/Helper/Avatars';
import {
  getTableSettingsStorageKey,
  TableSettings,
  useDebounce,
  useLocalStorageSettings,
} from 'hooks';
import { RelatedCompanyChip } from 'components/RelatedCompanyChip';
import { DateDisplay } from 'components/DateDisplay/DateDisplay';
import { PageableTableHeader } from 'components/PageableTable/components';
import { Alert, Autocomplete } from '@material-ui/lab';
import { endOfDay, startOfDay, startOfMonth } from 'date-fns';
import { useLocation } from 'react-router-dom';

// ─── Status config for attendee dialog ────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; icon: React.ReactElement }
> = {
  accepted: {
    label: 'Accepted',
    color: '#4caf50',
    icon: <CheckCircle style={{ color: '#4caf50' }} fontSize="small" />,
  },
  declined: {
    label: 'Declined',
    color: '#f44336',
    icon: <Cancel style={{ color: '#f44336' }} fontSize="small" />,
  },
  tentative: {
    label: 'Tentative',
    color: '#ff9800',
    icon: <Help style={{ color: '#ff9800' }} fontSize="small" />,
  },
  'needs action': {
    label: 'Pending',
    color: '#9e9e9e',
    icon: <Schedule style={{ color: '#9e9e9e' }} fontSize="small" />,
  },
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  datePickerFormControl: {
    marginBottom: theme.spacing(0.5),
  },
  dateColumn: {
    width: 120,
  },
  smallAvatar: {
    marginRight: theme.spacing(1),
    width: 20,
    height: 20,
    fontSize: 12,
  },
  itemIcon: {
    marginRight: theme.spacing(0.5),
  },
  accountManager: {
    display: 'flex',
    alignItems: 'center',
    gap: '1px',
    border: 'none',
    marginBottom: theme.spacing(0.5),
    marginLeft: 0,
  },
  formControl: {
    marginBottom: theme.spacing(0.5),
  },
  textControl: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      '& .MuiCardHeader-action': {
        marginRight: 0,
        marginTop: theme.spacing(2),
        alignSelf: 'stretch',
      },
    },
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  },
  cardTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  cardSubheader: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  attendeeChip: {
    fontWeight: 500,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
  },
  attendeeList: {
    maxHeight: 400,
    overflow: 'auto',
  },
  attendeeListIcon: {
    minWidth: 32,
  },
  statusChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  filterRow: {
    marginBottom: theme.spacing(1),
  },
  organizerCell: {
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    cursor: 'default',
  },
  organizerColumn: {
    maxWidth: 220,
  },
  infoDialog: {
    '& .MuiDialogContent-root': {
      paddingTop: theme.spacing(1),
    },
  },
  infoSection: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  infoSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  departmentChip: {
    margin: 2,
    maxWidth: 150,
  },
  individualChip: {
    margin: 2,
    maxWidth: 150,
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────

export const MeetingsView: React.FC = () => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [pageNumber, setPageNumber] = React.useState(0);
  const [showInfoDialog, setShowInfoDialog] = React.useState(false);

  const { pathname, hash } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );

  const today = React.useMemo(() => startOfDay(new Date()), []);
  const defaultDateFrom = React.useMemo(
    () => startOfMonth(today).toISOString(),
    [today],
  );
  const defaultDateTo = React.useMemo(
    () => endOfDay(today).toISOString(),
    [today],
  );

  const [dateFrom, setDateFrom] = React.useState<string | undefined>(
    defaultDateFrom,
  );
  const [dateTo, setDateTo] = React.useState<string | undefined>(defaultDateTo);
  const [data, setData] = React.useState<PagedMeeting | undefined>();
  const [textSearch, setTextSearch] = React.useState<string | undefined>();
  const debouncedTextSearch = useDebounce(textSearch, 400);
  const [selectedMeeting, setSelectedMeeting] = React.useState<
    Meeting | undefined
  >();
  const [selectedUnits, setSelectedUnits] = React.useState<string[]>([]);
  const [selectedIndividuals, setSelectedIndividuals] = React.useState<
    string[]
  >([]);
  const [hasCompany, setHasCompany] = React.useState<string>('All');
  const [filterOptions, setFilterOptions] = React.useState<
    MeetingFilterOptions | undefined
  >();

  // Load filter options on mount
  React.useEffect(() => {
    fetchMeetingFilterOptions()
      .then(setFilterOptions)
      .catch(() => {
        setFilterOptions({ units: [], individuals: [] });
      });
  }, []);

  // Build the unit_name param for the API: comma-separated or 'All'
  const unitNameParam = React.useMemo(() => {
    return selectedUnits.length > 0 ? selectedUnits.join(',') : 'All';
  }, [selectedUnits]);

  // Build the individual_name param for the API: comma-separated or undefined
  const individualNameParam = React.useMemo(() => {
    return selectedIndividuals.length > 0
      ? selectedIndividuals.join(',')
      : undefined;
  }, [selectedIndividuals]);

  const hasActiveFilters = React.useMemo(() => {
    return (
      selectedUnits.length > 0 ||
      selectedIndividuals.length > 0 ||
      hasCompany !== 'All' ||
      dateFrom !== defaultDateFrom ||
      dateTo !== defaultDateTo ||
      (textSearch && textSearch.length > 0)
    );
  }, [
    selectedUnits,
    selectedIndividuals,
    hasCompany,
    dateFrom,
    dateTo,
    textSearch,
    defaultDateFrom,
    defaultDateTo,
  ]);

  const resetFilters = React.useCallback(() => {
    setTextSearch(undefined);
    setSelectedUnits([]);
    setSelectedIndividuals([]);
    setHasCompany('All');
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setPageNumber(0);
  }, [defaultDateFrom, defaultDateTo]);

  const fetchData = React.useCallback(() => {
    setLoading(true);
    setError(false);
    fetchAllMeetings(
      pageNumber + 1,
      tableSettings.rowsPerPage,
      dateFrom,
      dateTo,
      debouncedTextSearch,
      unitNameParam,
      individualNameParam,
      hasCompany === 'All' ? undefined : hasCompany,
    )
      .then((d) => {
        setData(d);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    pageNumber,
    tableSettings.rowsPerPage,
    debouncedTextSearch,
    dateFrom,
    dateTo,
    unitNameParam,
    individualNameParam,
    hasCompany,
  ]);

  const exportData = React.useCallback(async () => {
    setExporting(true);
    snackbar.info('Preparing your export…');
    try {
      await exportMeetings(
        dateFrom,
        dateTo,
        debouncedTextSearch,
        unitNameParam,
        individualNameParam,
        hasCompany === 'All' ? undefined : hasCompany,
      );
      snackbar.success('Your export has downloaded.');
    } catch {
      snackbar.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [
    snackbar,
    debouncedTextSearch,
    dateFrom,
    dateTo,
    unitNameParam,
    individualNameParam,
    hasCompany,
  ]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setPageNumber(0);
  }, [
    debouncedTextSearch,
    dateFrom,
    dateTo,
    unitNameParam,
    individualNameParam,
    hasCompany,
  ]);

  const columns: PageableTableHeader[] = [
    {
      key: 'meeting_date',
      label: 'Date',
      sortable: false,
    },
    {
      key: 'meeting_title',
      label: 'Meeting',
      sortable: false,
    },
    {
      key: 'cams',
      label: 'Business Development',
      sortable: false,
    },
    {
      key: 'recruiters',
      label: 'Recruiter(s)',
      sortable: false,
    },
    {
      key: 'organiser',
      label: 'Organiser',
      sortable: false,
    },
    {
      key: 'individuals',
      label: 'Attendees',
      sortable: false,
    },
  ];

  const renderPersonList = (
    people: string | undefined,
    maxVisible: number = 2,
  ) => {
    if (!people) return null;
    const list = people.split(', ').filter(Boolean);
    const visible = list.slice(0, maxVisible);
    const remaining = list.length - maxVisible;

    return (
      <Box display="flex" flexDirection="column">
        {visible.map((person, index) => (
          <Box key={index} className={classes.accountManager}>
            <Avatar className={classes.smallAvatar} {...stringAvatar(person)} />
            <Typography variant="body2">{person}</Typography>
          </Box>
        ))}
        {remaining > 0 && (
          <Tooltip title={list.slice(maxVisible).join(', ')}>
            <Box className={classes.accountManager}>
              <Avatar className={classes.smallAvatar}>+{remaining}</Avatar>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  const parseAttendee = (
    raw: string,
  ): { email: string; status: string | undefined } => {
    const trimmed = raw.trim();
    const match = trimmed.match(/^(.+?)\s*\((.+?)\)$/);
    return {
      email: match ? match[1].trim() : trimmed,
      status: match ? match[2].trim().toLowerCase() : undefined,
    };
  };

  const getStatusConfig = (status: string | undefined) => {
    if (!status) return STATUS_CONFIG['needs action'];
    return STATUS_CONFIG[status] ?? STATUS_CONFIG['needs action'];
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        classes={{
          root: classes.cardHeader,
          title: classes.cardTitle,
          subheader: classes.cardSubheader,
        }}
        title={
          <Box display="flex" alignItems="center">
            KonnektTalexio Meetings
            <IconButton
              size="small"
              aria-label="About this page"
              onClick={() => setShowInfoDialog(true)}
            >
              <InfoOutlined fontSize="small" style={{ color: '#9e9e9e' }} />
            </IconButton>
          </Box>
        }
        subheader="External client meetings across all KonnektTalexio team members. Data synced nightly from Google Calendar."
        action={
          <div className={classes.headerActions}>
            <Button
              color="primary"
              href="https://calendar.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<LaunchIcon />}
            >
              Google Calendar
            </Button>
            <Button color="secondary" onClick={exportData} disabled={exporting}>
              Export Data
              {exporting && (
                <Box ml={1} display="flex">
                  <CircularProgress size={16} />
                </Box>
              )}
            </Button>
          </div>
        }
      />
      <CardContent style={{ paddingTop: 0, paddingBottom: 8 }}>
        <TextField
          className={classes.textControl}
          value={textSearch ?? ''}
          variant="outlined"
          autoFocus
          onChange={(e) => setTextSearch(e.currentTarget.value || undefined)}
          fullWidth
          placeholder="Search by meeting title, individual, organiser, attendee email, or company"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Grid container spacing={2} className={classes.filterRow}>
          <Grid item xs={12} md={3}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                multiple
                options={filterOptions?.individuals ?? []}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Team Member(s)"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      selectedIndividuals.length === 0 ? 'All members' : ''
                    }
                  />
                )}
                value={selectedIndividuals}
                onChange={(_e, newValue) => {
                  setSelectedIndividuals(newValue);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      className={classes.individualChip}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                limitTags={2}
                disableCloseOnSelect
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                multiple
                options={filterOptions?.units ?? []}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Department(s)"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      selectedUnits.length === 0 ? 'All departments' : ''
                    }
                  />
                )}
                value={selectedUnits}
                onChange={(_e, newValue) => {
                  setSelectedUnits(newValue);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      className={classes.departmentChip}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                limitTags={2}
                disableCloseOnSelect
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                options={['All', 'yes', 'no']}
                getOptionLabel={(option) => {
                  if (option === 'yes') return 'Linked';
                  if (option === 'no') return 'Not linked';
                  return 'All';
                }}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Company Link"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                disableClearable={true}
                value={hasCompany}
                onChange={(_e, newValue) => {
                  setHasCompany(newValue);
                }}
              />
            </FormControl>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Matched via Zendesk contacts.{' '}
              <Tooltip
                title="A meeting shows as 'Not linked' when no external attendee's email matches a contact in Zendesk. Add the email to a Zendesk company to link it."
                placement="bottom"
                arrow
              >
                <span
                  style={{
                    textDecoration: 'underline',
                    cursor: 'help',
                  }}
                >
                  Learn more
                </span>
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl className={classes.datePickerFormControl} fullWidth>
              <DatePicker
                label="Date from"
                margin="none"
                fullWidth
                value={dateFrom}
                inputVariant="outlined"
                InputLabelProps={{ shrink: true }}
                onChange={(date: MaterialUiPickersDate) => {
                  setDateFrom(date ? date.toISOString() : undefined);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl className={classes.datePickerFormControl} fullWidth>
              <DatePicker
                label="Date to"
                margin="none"
                fullWidth
                value={dateTo}
                inputVariant="outlined"
                InputLabelProps={{ shrink: true }}
                onChange={(date: MaterialUiPickersDate) => {
                  setDateTo(date ? date.toISOString() : undefined);
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<RotateLeft />}
            onClick={resetFilters}
            style={{ marginBottom: 4 }}
          >
            Reset Filters
          </Button>
        )}
      </CardContent>
      {error ? (
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchData}>
                Retry
              </Button>
            }
          >
            Could not load meetings. Check your connection and try again.
          </Alert>
        </CardContent>
      ) : (
        <PageableTable
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          showSkeletonLoading={true}
          rows={
            data?.data?.map((d: Meeting, index: number) => {
              return {
                key: index.toString(),
                cells: [
                  {
                    key: 'meeting_date',
                    props: {
                      className: classes.dateColumn,
                    },
                    display: d.meeting_date && d.meeting_date !== 'NaT' && (
                      <DateDisplay date={d.meeting_date} />
                    ),
                  },
                  {
                    key: 'meeting_title',
                    display: (
                      <React.Fragment>
                        <Typography>{d.meeting_title}</Typography>
                        {d.company_id && d.company_title ? (
                          <RelatedCompanyChip
                            companyId={d.company_id.split(',')[0].trim()}
                            companyName={d.company_title.split(',')[0].trim()}
                          />
                        ) : null}
                      </React.Fragment>
                    ),
                  },
                  {
                    key: 'cams',
                    display: renderPersonList(d.cams),
                  },
                  {
                    key: 'recruiters',
                    display: renderPersonList(d.recruiters),
                  },
                  {
                    key: 'organiser',
                    props: {
                      className: classes.organizerColumn,
                    },
                    display: d.organizer ? (
                      <Tooltip title={d.organizer} placement="top">
                        <Typography
                          variant="body2"
                          className={classes.organizerCell}
                        >
                          {d.organizer}
                        </Typography>
                      </Tooltip>
                    ) : null,
                  },
                  {
                    key: 'individuals',
                    display: (
                      <Chip
                        className={classes.attendeeChip}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label={`${
                          d.attendee_emails?.split(',').length || 0
                        } Attendees`}
                        onClick={() => {
                          setSelectedMeeting(d);
                        }}
                        clickable
                      />
                    ),
                  },
                ],
              };
            }) ?? []
          }
          loading={loading}
          pageNumber={pageNumber}
          rowCount={data?.total_count ?? 0}
          columns={columns}
          emptyTableText="No meetings match these filters. Try widening the date range or clearing a filter."
          onChangePage={setPageNumber}
        />
      )}

      {/* ── Attendee Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(selectedMeeting)}
        onClose={() => setSelectedMeeting(undefined)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Box>
            <Typography variant="h6">
              {selectedMeeting?.meeting_title ?? 'Meeting'}
            </Typography>
            {selectedMeeting?.meeting_date &&
              selectedMeeting.meeting_date !== 'NaT' && (
                <Typography variant="body2" color="textSecondary">
                  {new Date(selectedMeeting.meeting_date).toLocaleDateString(
                    'en-GB',
                    {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    },
                  )}
                </Typography>
              )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedMeeting(undefined)}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMeeting?.organizer && (
            <Box mb={1}>
              <Typography variant="caption" color="textSecondary">
                Organised by
              </Typography>
              <Typography variant="body2">
                {selectedMeeting.organizer}
              </Typography>
            </Box>
          )}
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ marginBottom: 4, display: 'block' }}
          >
            {selectedMeeting?.attendee_emails?.split(',').length || 0}{' '}
            attendee(s) &middot; RSVP status from Google Calendar
          </Typography>
          <List dense className={classes.attendeeList} disablePadding>
            {selectedMeeting?.attendee_emails.split(',').map((a, index) => {
              const { email, status } = parseAttendee(a);
              const config = getStatusConfig(status);

              return (
                <ListItem key={index} disableGutters>
                  <ListItemIcon className={classes.attendeeListIcon}>
                    <Tooltip title={config.label}>{config.icon}</Tooltip>
                  </ListItemIcon>
                  <ListItemText
                    primary={email}
                    secondary={
                      <Chip
                        label={config.label}
                        size="small"
                        className={classes.statusChip}
                        style={{
                          color: config.color,
                          borderColor: config.color,
                        }}
                        variant="outlined"
                      />
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      {/* ── Info Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        maxWidth="sm"
        fullWidth
        className={classes.infoDialog}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">About this page</Typography>
          <IconButton size="small" onClick={() => setShowInfoDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.infoSection}>
            <Typography
              variant="subtitle2"
              className={classes.infoSectionTitle}
            >
              Data Source
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Meeting data is synced nightly from Google Calendar via service
              accounts. Only external meetings are shown &mdash; internal-only
              meetings (where all attendees are KonnektTalexio employees) are
              excluded automatically.
            </Typography>
          </div>

          <div className={classes.infoSection}>
            <Typography
              variant="subtitle2"
              className={classes.infoSectionTitle}
            >
              What&apos;s excluded
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Meetings with titles containing &quot;interview&quot;,
              &quot;int.&quot;, &quot;weekly&quot;, or &quot;fortnightly&quot;
              are filtered out, along with any blacklisted organisers,
              attendees, or events.
            </Typography>
          </div>

          <div className={classes.infoSection}>
            <Typography
              variant="subtitle2"
              className={classes.infoSectionTitle}
            >
              CAM(s) vs Recruiter(s)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              These columns are based on the attendee&apos;s department.
              Business Development team members appear under CAM(s). All other
              departments (Finance &amp; Legal, General, Tech, Candidate
              Engagement, Cell teams, etc.) appear under Recruiter(s). This is
              based on each employee&apos;s latest position in the HR system.
            </Typography>
          </div>

          <div className={classes.infoSection}>
            <Typography
              variant="subtitle2"
              className={classes.infoSectionTitle}
            >
              Company Link
            </Typography>
            <Typography variant="body2" color="textSecondary">
              A company is linked to a meeting when an external attendee&apos;s
              email address matches a contact in Zendesk that belongs to a
              company. If a meeting shows no company, it means none of the
              external attendees&apos; emails were found in Zendesk. To fix
              this, add the contact&apos;s email to the appropriate company in
              Zendesk &mdash; it will be picked up on the next nightly sync.
            </Typography>
          </div>

          <div className={classes.infoSection}>
            <Typography
              variant="subtitle2"
              className={classes.infoSectionTitle}
            >
              Organiser
            </Typography>
            <Typography variant="body2" color="textSecondary">
              This is the email of whoever created the Google Calendar event. It
              may be a client&apos;s email if they organised the meeting, not
              necessarily a KonnektTalexio team member.
            </Typography>
          </div>

          <div className={classes.infoSection}>
            <Typography
              variant="subtitle2"
              className={classes.infoSectionTitle}
            >
              RSVP Status
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Attendee statuses (Accepted, Declined, Tentative, Pending) come
              directly from Google Calendar responses. &quot;Pending&quot; means
              the attendee hasn&apos;t responded yet.
            </Typography>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
